<template>
  <span class="svg-icon" :class="{ 'apply-color': applyColor }" :style="iconStyle" v-html="rawSvg"></span>
</template>

<script setup>
// 批量导入所有 SVG 文件 (使用相对路径，因为 import.meta.glob 不支持别名)
const svgModules = import.meta.glob('../../assets/svg/**/*.svg', {
  query: '?raw',
  import: 'default',
  eager: true,
})

// 构建 SVG 名称映射表
const svgMap = Object.keys(svgModules).reduce((acc, path) => {
  // 提取文件名作为 key，例如 ../../assets/svg/tabbar/home.svg -> tabbar/home
  const match = path.match(/assets\/svg\/(.*)\.svg$/)
  if (match) {
    const name = match[1]
    acc[name] = svgModules[path]
  }
  return acc
}, {})

const props = defineProps({
  // SVG 名称，支持路径格式，如 'tabbar/home'、'brand'、'plus'
  name: {
    type: String,
    default: '',
  },
  // 直接传入 SVG 字符串（兼容旧方式）
  svg: {
    type: String,
    default: '',
  },
  // 是否应用颜色（通过 CSS 设置 currentColor）
  applyColor: {
    type: Boolean,
    default: false,
  },
  // 颜色（仅在 applyColor 为 true 时生效）
  color: {
    type: String,
    default: '',
  },
  // 尺寸（宽高相同）
  size: {
    type: [String, Number],
    default: '',
  },
  // 宽度
  width: {
    type: [String, Number],
    default: '',
  },
  // 高度
  height: {
    type: [String, Number],
    default: '',
  },
})

// 获取原始 SVG 内容
const rawSvg = computed(() => {
  // 优先使用直接传入的 svg
  if (props.svg) {
    return props.svg
  }
  // 通过名称获取
  if (props.name && svgMap[props.name]) {
    return svgMap[props.name]
  }
  console.warn(`[SvgIcon] SVG not found: ${props.name}`)
  return ''
})

// 处理尺寸值
const formatSize = (val) => {
  if (!val) return ''
  if (typeof val === 'number') return `${val}px`
  // 如果已经是带单位的字符串，直接返回
  if (/^\d+(px|em|rem|%|vh|vw)$/.test(val)) return val
  // 纯数字字符串，添加 px
  if (/^\d+$/.test(val)) return `${val}px`
  return val
}

// 图标样式
const iconStyle = computed(() => {
  const style = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  }

  // 仅在 applyColor 为 true 时设置颜色
  if (props.applyColor && props.color) {
    style.color = props.color
  }

  // 设置宽高
  if (props.size) {
    const size = formatSize(props.size)
    style.width = size
    style.height = size
  } else {
    if (props.width) style.width = formatSize(props.width)
    if (props.height) style.height = formatSize(props.height)
  }

  return style
})
</script>

<style scoped lang="less">
.svg-icon {
  :deep(svg) {
    width: 100%;
    height: 100%;
  }

  // 仅在 applyColor 为 true 时通过 CSS 应用颜色
  &.apply-color {
    :deep(svg) {
      fill: currentColor;
    }
  }
}
</style>
